import React, { useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  message,
  Popconfirm,
  Tooltip,
  Typography,
  Alert,
  Result,
} from 'antd'
import {
  SearchOutlined,
  ReloadOutlined,
  ExportOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store'
import {
  fetchSubscriptions,
  updateSubscriptionStatus,
  batchUpdateStatus,
  fetchStatistics,
  setFilters,
  setSelectedSubscriptions,
  clearSelected,
} from '@store/slices/subscriptionSlice'
import { subscriptionService } from '@services/subscriptionService'
import dayjs from 'dayjs'

const { Title } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; errorMsg: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, errorMsg: '' }
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMsg: error?.message || '未知渲染错误' }
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[SubscriptionPage] render crash:', error, info)
  }
  render() {
    if (this.state.hasError) {
      return (
        <Result
          status="error"
          title="页面渲染异常"
          subTitle={this.state.errorMsg}
          extra={<Button type="primary" onClick={() => this.setState({ hasError: false, errorMsg: '' })}>重试</Button>}
        />
      )
    }
    return this.props.children
  }
}

const SubscriptionManagement: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { subscriptions, loading, error, filters, pagination, selectedSubscriptions, statistics } = useAppSelector(
    (state) => state.subscriptions
  )

  const [searchText, setSearchText] = useState('')
  const [exportLoading, setExportLoading] = useState(false)
  const [batchModalVisible, setBatchModalVisible] = useState(false)
  const [batchStatus, setBatchStatus] = useState<'active' | 'expired' | 'cancelled'>('active')

  useEffect(() => {
    dispatch(fetchSubscriptions(filters))
    dispatch(fetchStatistics(filters))
  }, [dispatch, filters])

  // 处理搜索
  const handleSearch = () => {
    const next = { ...filters, search: searchText.trim(), page: 1 }
    dispatch(setFilters(next))
    dispatch(fetchSubscriptions(next))
    dispatch(fetchStatistics(next))
  }

  // 处理重置
  const handleReset = () => {
    const resetFilters = {
      status: 'all' as const,
      gardenId: 'all',
      userId: 'all',
      startDate: '',
      endDate: '',
      search: '',
      page: 1,
      pageSize: 10,
    }
    dispatch(setFilters(resetFilters))
    dispatch(fetchSubscriptions(resetFilters))
    setSearchText('')
  }

  // 处理分页
  const handleTableChange = (page: number, pageSize: number) => {
    dispatch(setFilters({ ...filters, page, pageSize }))
    dispatch(fetchSubscriptions({ ...filters, page, pageSize }))
  }

  // 处理状态更新
  const handleStatusUpdate = async (id: string, status: 'active' | 'expired' | 'cancelled') => {
    try {
      await dispatch(updateSubscriptionStatus({ id, status })).unwrap()
      message.success('状态更新成功')
      dispatch(fetchStatistics(filters))
    } catch (error: any) {
      message.error(error || '状态更新失败')
    }
  }

  // 处理批量状态更新
  const handleBatchStatusUpdate = async () => {
    if (selectedSubscriptions.length === 0) {
      message.warning('请选择要更新的订阅')
      return
    }

    try {
      await dispatch(batchUpdateStatus({ ids: selectedSubscriptions, status: batchStatus })).unwrap()
      message.success('批量更新成功')
      setBatchModalVisible(false)
      dispatch(clearSelected())
      dispatch(fetchStatistics(filters))
    } catch (error: any) {
      message.error(error || '批量更新失败')
    }
  }

  // 处理导出
  const handleExport = async (format: 'excel' | 'csv' | 'pdf') => {
    setExportLoading(true)
    try {
      await subscriptionService.exportSubscriptions(filters, format)
      message.success('已开始下载 CSV 文件')
    } catch (error: any) {
      message.error(error?.message || error || '导出失败')
    } finally {
      setExportLoading(false)
    }
  }

  // 获取状态标签
  const getStatusTag = (status: string, record?: any) => {
    const s = String(status || '').toLowerCase()
    if (record?.needsRenewal && s === 'active') {
      return <Tag color="warning">待续费</Tag>
    }
    const statusMap: Record<string, { color: string; text: string }> = {
      active: { color: 'green', text: '进行中' },
      expired: { color: 'orange', text: '已过期' },
      cancelled: { color: 'red', text: '已取消' },
      pending: { color: 'blue', text: '待生效' },
    }
    const config = statusMap[s] || { color: 'default', text: status }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  // 表格列定义
  const columns = [
    {
      title: '订阅ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace' }}>{text ? text.slice(0, 8) : '—'}...</span>
        </Tooltip>
      ),
    },
    {
      title: '用户',
      dataIndex: 'user',
      key: 'user',
      width: 120,
      render: (user: any) => (
        <div>
          <div>{user?.nickname || '未知用户'}</div>
          {user?.phone && <div style={{ fontSize: '12px', color: '#999' }}>{user?.phone}</div>}
        </div>
      ),
    },
    {
      title: '菜园',
      dataIndex: 'garden',
      key: 'garden',
      width: 150,
      render: (garden: any) => garden?.name || '未知菜园',
    },
    {
      title: '套餐类型',
      dataIndex: 'planType',
      key: 'planType',
      width: 100,
      render: (planType: string) => {
        const planMap = {
          monthly4: '月付-配送4次/每周1次',
          monthly8: '月付-配送8次/每周2次',
          monthly12: '月付12次',
          quarterly: '季付',
          yearly: '年付',
        }
        return planMap[planType as keyof typeof planMap] || planType
      },
    },
    {
      title: '月费',
      dataIndex: 'monthlyPrice',
      key: 'monthlyPrice',
      width: 100,
      render: (price: any) => (price != null ? `¥${Number(price).toFixed(2)}` : '—'),
    },
    {
      title: '订阅日期',
      dataIndex: 'subscriptionDate',
      key: 'subscriptionDate',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '—',
    },
    {
      title: '到期日期',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '—',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string, record: any) => getStatusTag(status, record),
    },
    {
      title: '到期提醒',
      key: 'expiryReminder',
      width: 150,
      render: (_: any, record: any) => (
        <Space size={4}>
          {record.slotLocked && <Tag color="blue">名额锁定</Tag>}
          {record.daysUntilExpiry !== null && record.daysUntilExpiry <= 7 && record.daysUntilExpiry > 0 && (
            <Tag color={record.daysUntilExpiry <= 3 ? 'red' : 'orange'}>
              {record.daysUntilExpiry <= 0 ? '已到期' : `${record.daysUntilExpiry}天后到期`}
            </Tag>
          )}
          {record.daysUntilExpiry !== null && record.daysUntilExpiry <= 0 && record.status === 'active' && (
            <Tag color="red">已到期</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '自动续费',
      dataIndex: 'autoRenewal',
      key: 'autoRenewal',
      width: 100,
      render: (autoRenewal: boolean) => (
        <Tag color={autoRenewal ? 'green' : 'default'}>
          {autoRenewal ? '开启' : '关闭'}
        </Tag>
      ),
    },
    {
      title: '配送次数',
      dataIndex: 'totalDeliveries',
      key: 'totalDeliveries',
      width: 100,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record: any) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => navigate(`/subscriptions/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button type="link" icon={<EditOutlined />} size="small" />
          </Tooltip>
          {record.status === 'active' && (
            <Popconfirm
              title="确定要取消这个订阅吗？"
              onConfirm={() => handleStatusUpdate(record.id, 'cancelled')}
            >
              <Tooltip title="取消订阅">
                <Button type="link" icon={<CloseCircleOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
          )}
          {record.status === 'expired' && (
            <Popconfirm
              title="确定要激活这个订阅吗？"
              onConfirm={() => handleStatusUpdate(record.id, 'active')}
            >
              <Tooltip title="激活订阅">
                <Button type="link" icon={<CheckCircleOutlined />} size="small" />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <ErrorBoundary>
      <div>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ margin: 0 }}>
            订阅管理
          </Title>
        <Typography.Text type="secondary">
          管理所有用户订阅信息，包括状态更新、数据导出等功能
        </Typography.Text>
      </div>

      {error && (
        <Alert
          message="数据加载失败"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="总订阅数" value={statistics?.total ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="活跃订阅" value={statistics?.active ?? 0} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="待续费" value={statistics?.needsRenewal ?? 0} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="已过期" value={statistics?.expired ?? 0} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="总收入" value={statistics?.totalRevenue ?? 0} precision={2} prefix="¥" />
          </Card>
        </Col>
      </Row>

      {/* 筛选和操作区域 */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          {/* 第一行：筛选条件 */}
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="搜索用户或菜园"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              value={filters.status}
              onChange={(value) => dispatch(setFilters({ ...filters, status: value }))}
              style={{ width: '100%' }}
            >
              <Option value="all">全部状态</Option>
              <Option value="active">进行中</Option>
              <Option value="needsRenewal">待续费</Option>
              <Option value="expired">已过期</Option>
              <Option value="cancelled">已取消</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={10}>
            <RangePicker
              value={filters.startDate && filters.endDate ? [dayjs(filters.startDate), dayjs(filters.endDate)] : null}
              onChange={(dates) => {
                if (dates) {
                  dispatch(setFilters({
                    ...filters,
                    startDate: dates[0]?.format('YYYY-MM-DD') || '',
                    endDate: dates[1]?.format('YYYY-MM-DD') || '',
                  }))
                } else {
                  dispatch(setFilters({ ...filters, startDate: '', endDate: '' }))
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          
          {/* 第二行：操作按钮 */}
          <Col xs={24}>
            <Space wrap>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button
                icon={<ExportOutlined />}
                loading={exportLoading}
                onClick={() => handleExport('excel')}
              >
                导出
              </Button>
              {selectedSubscriptions.length > 0 && (
                <Button
                  type="primary"
                  danger
                  onClick={() => setBatchModalVisible(true)}
                >
                  批量操作 ({selectedSubscriptions.length})
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 订阅列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={subscriptions || []}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination?.page || 1,
            pageSize: pagination?.pageSize || 10,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
          rowSelection={{
            selectedRowKeys: selectedSubscriptions,
            onChange: (selectedRowKeys) => {
              dispatch(setSelectedSubscriptions(selectedRowKeys as string[]))
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 批量操作模态框 */}
      <Modal
        title="批量操作"
        open={batchModalVisible}
        onOk={handleBatchStatusUpdate}
        onCancel={() => setBatchModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <p>已选择 {selectedSubscriptions.length} 个订阅</p>
        <p>请选择要更新的状态：</p>
        <Select
          value={batchStatus}
          onChange={setBatchStatus}
          style={{ width: '100%' }}
        >
          <Option value="active">激活</Option>
          <Option value="expired">设为过期</Option>
          <Option value="cancelled">取消</Option>
        </Select>
      </Modal>
    </div>
    </ErrorBoundary>
  )
}

export default SubscriptionManagement
